import { useEffect, useRef, useState } from "react";
import { Upload, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/media").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        await api.post("/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      }
      load();
      toast.success("Изображенията са качени");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const saveMeta = async () => {
    if (!selected) return;
    try {
      await api.patch(`/media/${selected.id}`, { alt_text: selected.alt_text, caption: selected.caption });
      toast.success("Запазено");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Изтриване на изображението?")) return;
    await api.delete(`/media/${id}`);
    setSelected(null);
    load();
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl font-bold text-[#1A1A1A]">Медийна библиотека</h1>
        <Button onClick={() => fileRef.current.click()} disabled={uploading} className="bg-[#A6192E] hover:bg-[#8C1123]" data-testid="admin-media-upload-btn">
          <Upload className="w-4 h-4 mr-2" /> {uploading ? "Качване..." : "Качи изображения"}
        </Button>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} data-testid="admin-media-file-input" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="admin-media-grid">
          {items.map((m) => (
            <button
              key={m.id}
              onClick={() => setSelected(m)}
              className={`aspect-square overflow-hidden border-2 ${selected?.id === m.id ? "border-[#A6192E]" : "border-transparent"}`}
              data-testid={`admin-media-item-${m.id}`}
            >
              <img src={`${BACKEND_URL}${m.url}`} alt={m.alt_text} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
        <div className="lg:col-span-1">
          {selected ? (
            <div className="bg-white border border-[#E2E8F0] p-4 space-y-3" data-testid="admin-media-detail-panel">
              <img src={`${BACKEND_URL}${selected.url}`} alt="" className="w-full aspect-video object-cover" />
              <Input placeholder="Alt текст" value={selected.alt_text || ""} onChange={(e) => setSelected({ ...selected, alt_text: e.target.value })} data-testid="admin-media-alt-input" />
              <Input placeholder="Caption" value={selected.caption || ""} onChange={(e) => setSelected({ ...selected, caption: e.target.value })} data-testid="admin-media-caption-input" />
              <Button onClick={saveMeta} className="w-full bg-[#1A1A1A]" data-testid="admin-media-save-btn">Запази</Button>
              <Button onClick={() => remove(selected.id)} variant="outline" className="w-full text-red-600 border-red-200" data-testid="admin-media-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Изтрий
              </Button>
            </div>
          ) : (
            <p className="text-sm text-[#718096]">Избери изображение за преглед и редакция.</p>
          )}
        </div>
      </div>
    </div>
  );
}
